import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { report243FilterOptions } from "../report-243";
import { Report243FilterSettingsForm } from "../settings-form/Report243FilterSettingsForm";
import { Report243FilterSettings } from "../settings/Report243FilterSettings";
import { Report243Row } from "../table-data/Report243Row";
import { Report243JournalRequest } from "../journal/Report243JournalRequest";
import { Option } from "@/modules/budget/monitoring/reports-constructor/common/types/Option";
import { Report243TableDataRequest } from "../data-request/Report243TableDataRequest";
import { MutuallyRedeemingLevel } from "../../../common/types/roots/fields";

export class Report243Facade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(report243FilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== '2-43') {
            throw new Error('не соответсвующий подтип FilterSettings')
        }
        if (options.key !== '2-43') {
            throw new Error('не соответсвующий подтип FilterOptions')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('Не найден reportName')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('Не найден reportType')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('Не найден classificationType')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('Не найден dataSource')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('Не найден periodicity')
        }
        periodicity = { ...periodicity }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('Не найден measureUnitOption')
        }
        measureUnitOption = { ...measureUnitOption }



        const regions: Option<string>[] = []
        settings.regions.forEach(region => {
            const option = options.regions.find(it => it.value === region)!
            regions.push({...option})
        })

        const byFormOptions = options.byForms.find(it => settings.byForm === (it.value))
        if (!byFormOptions) {
            throw new Error('Не найден byFormOption')
        }

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }
        const topN: boolean = settings.topNCount != null

        // ВЗК и уровень дохода: в старых шаблонах полей нет — значения по умолчанию (RQ.eAk.F243.01.09.10)
        const mutuallyRedeemingValue = settings.mutuallyRedeeming ?? 'WITHOUT'
        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === mutuallyRedeemingValue)
            ?? options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')
        if (!mutuallyRedeemingOption) {
            throw new Error('Не найден mutuallyRedeemingOption')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = mutuallyRedeemingOption.value === 'WITH'
            ? []
            : cloneDeep(options.mutuallyRedeemingLevels.filter(it => (settings.mutuallyRedeemingLevels ?? []).includes(it.value)))

        let incomeLevelOption = options.incomeLevels.find(it => it.value === (settings.incomeLevel ?? '00'))
            ?? options.incomeLevels.find(it => it.value === '00')
        if (!incomeLevelOption) {
            throw new Error('Не найден incomeLevelOption')
        }
        incomeLevelOption = { ...incomeLevelOption }


        // 2. Формирование
        const form: Report243FilterSettingsForm = {
            // Base:
            key: '2-43',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:

            regions: regions,
            byForm: byFormOptions,
            incomes: cloneDeep(settings.incomes),
            segmentations: cloneDeep(settings.segmentations),
            periodFrom: new Date(settings.period.from),
            periodTo: new Date(settings.period.to),
            comparePrevYear: settings.comparePrevYear,
            periodPrevFrom: new Date(settings.periodPrev.from),
            periodPrevTo: new Date(settings.periodPrev.to),
            senderbin: cloneDeep(options.senderbin.filter(it => settings.senderbin?.includes(it.value))),
            oked: cloneDeep(options.oked.filter(it => settings.oked?.includes(it.value))),
            kfs: cloneDeep(options.kfs.filter(it => settings.kfs?.includes(it.value))),
            kse: cloneDeep(options.kse.filter(it => settings.kse?.includes(it.value))),
            krp: cloneDeep(options.krp.filter(it => settings.krp?.includes(it.value))),
            indicators: cloneDeep(settings.indicators),
            measureUnit: measureUnitOption,
            isTree: cloneDeep(settings.isTree),
            byBudgetLevel: settings.byBudgetLevel ?? false,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            topN: topN,
            topNCount: settings.topNCount,
            fillCbc: cloneDeep(settings.fillCbc),
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            incomeLevel: incomeLevelOption

        };

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== '2-43') {
            throw new Error('не соответсвующий подтип FilterSettingsForm')
        }


        if (!form.measureUnit) {
            throw new Error('Необходимо ввести "Единица измерения"')
        }

        if (!form.regions?.length) {
            throw new Error('Необходимо ввести "Регион"')
        }

        if (!form.byForm) {
            throw new Error('Необходимо ввести "По форме"')
        }

        if (!form.mutuallyRedeeming) {
            throw new Error('Необходимо ввести "Взаимопогашаемые коды"')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }
        let topNCount: number | null = null
        if (form.topN) {
            topNCount = form.topNCount ?? null
        }

        const strPeriodFrom = moment(form.periodFrom).format('YYYY-MM-DD')
        const strPeriodTo = moment(form.periodTo).format('YYYY-MM-DD')
        const strPeriodPrevFrom = moment(form.periodPrevFrom).format('YYYY-MM-DD')
        const strPeriodPrevTo = moment(form.periodPrevTo).format('YYYY-MM-DD')

        const settings: Report243FilterSettings = {
            // Base:
            reportName: '2-43',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:

            regions: form.regions.map(it => it.value),
            byForm: form.byForm.value,
            incomes: cloneDeep(form.incomes),
            segmentations: form.segmentations,
            period: { from: strPeriodFrom, to: strPeriodTo },
            periodPrev: { from: strPeriodPrevFrom, to: strPeriodPrevTo },
            comparePrevYear: form.comparePrevYear,
            senderbin: form.senderbin.map(it => it.value),
            oked: form.oked.map(it => it.value),
            kfs: form.kfs.map(it => it.value),
            kse: form.kse.map(it => it.value),
            krp: form.krp.map(it => it.value),
            indicators: cloneDeep(form.indicators),
            measureUnit: form.measureUnit.value,
            isTree: form.isTree,
            byBudgetLevel: form.byBudgetLevel,
            roundUpTo: roundUpTo,
            topNCount: topNCount,
            fillCbc: form.fillCbc,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeeming.value === 'WITH'
                ? []
                : (form.mutuallyRedeemingLevels ?? []).map(it => it.value),
            incomeLevel: form.incomeLevel?.value ?? '00'
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(report243FilterOptions)
        const todayDate = moment().toDate()
        const firstJan = moment().startOf('year').toDate()
        const prevYear = todayDate.getFullYear() - 1
        const prevMonth = todayDate.getMonth()
        const firstJanPrev: Date = new Date(prevYear, 0, 1)
        let prevDate = todayDate.getDate()
        if (prevMonth === 1 && prevDate === 29 && prevYear % 4) {
            prevDate = 28
        }
        const todayPrev: Date = new Date(prevYear, prevMonth, prevDate)
        const byFormOption = options.byForms.find(it => '2-43' === (it.value))!
        const emptyForm: Report243FilterSettingsForm = {
            key: '2-43',
            // Base fields
            reportName: options.reportNames.find(it => it.value === '2-43')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'INCOMES')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'PERIOD')!,
            // Custom fields

            regions: [],
            byForm: byFormOption,
            incomes: cloneDeep(options.incomes),
            segmentations: cloneDeep(options.segmentations),
            periodFrom: firstJan,
            periodTo: todayDate,
            comparePrevYear: false,
            periodPrevFrom: firstJanPrev,
            periodPrevTo: todayPrev,
            senderbin: [],
            oked: [],
            kfs: [],
            kse: [],
            krp: [],
            indicators: cloneDeep(options.indicators),
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            isTree: true,
            byBudgetLevel: false,
            roundUp: false,
            roundUpTo: null,
            topN: false,
            topNCount: null,
            fillCbc: false,
            // Для 2-43 по умолчанию всегда «без ВЗК», без зависимости от региона
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            mutuallyRedeemingLevels: [],
            incomeLevel: options.incomeLevels.find(it => it.value === '00')!
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== '2-43') {
            throw new Error('не соответсвующий подтип FilterSettingsForm')
        }


        if (!form.measureUnit) {
            throw new Error('Необходимо ввести "Единица измерения"')
        }
        if (!form.regions?.length) {
            throw new Error('Необходимо ввести "Регион"')
        }
        if (!form.segmentations?.filter(it => it.active).length) {
            throw new Error('Необходимо ввести "Разрезность"')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('Необходимо ввести "Взаимопогашаемые коды"')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }
        let topNCount: number | null = null
        if (form.topN) {
            topNCount = form.topNCount ?? null
        }

        if (!['LOADER', 'IISK'].includes(form.dataSource.value)) {
            throw new Error('Неподдерживаемый источник данных')
        }
        const strPeriodFrom = moment(form.periodFrom).format('YYYY-MM-DD')
        const strPeriodTo = moment(form.periodTo).format('YYYY-MM-DD')
        const strPeriodPrevFrom = moment(form.periodPrevFrom).format('YYYY-MM-DD')
        const strPeriodPrevTo = moment(form.periodPrevTo).format('YYYY-MM-DD')
        const params: Report243TableDataRequest = {
            user_id: userId,
            incomes: form.incomes,
            source: form.dataSource.value,
            regions: form.regions.map(r => r.value),
            segmentations: form.segmentations?.filter(it => it.active).map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            top_n_count: topNCount,
            by_budget_level: form.byBudgetLevel ?? false,
            senderbin: form.senderbin.map(it => it.value),
            oked: form.oked.map(it => it.value),
            kfs: form.kfs.map(it => it.value),
            kse: form.kse.map(it => it.value),
            krp: form.krp.map(it => it.value),
            period_from: strPeriodFrom,
            period_to: strPeriodTo,
            period_prev_from: strPeriodPrevFrom,
            period_prev_to: strPeriodPrevTo,
            compare_prev_year: form.comparePrevYear,
            locale: i18nService.locale,
            fill_cbc: form.fillCbc,
            tree_view: form.isTree,
            vzk: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: form.mutuallyRedeeming.value === 'WITH'
                ? []
                : (form.mutuallyRedeemingLevels ?? []).map(it => it.value),
            budget_type: form.incomeLevel?.value ?? '00'
        }

        const indicatorsOrder: string[] = form.indicators.filter(it => it.active).map(it => it.key)

        const response = await fetch(`/api-py/monitoring/reports-constructor/2-43`,
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('Не удалось получить данные для отчета')
        }

        const tableData: Report243Row[] = await response.json()

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: Report243JournalRequest = {
            reportName: "2-43",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            indicators_order: indicatorsOrder, // Может быть редактирована во время превью
            params: params,
            locale: i18nService.locale,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }

    // EAK-1904: перестроение сформированного отчёта из кеша сессии без чтения БД (кеш истёк — бэк пересчитает полностью)
    async rebuildReport(params: Report243TableDataRequest): Promise<Report243Row[]> {
        const response = await fetch(`/api-py/monitoring/reports-constructor/2-43/rebuild`,
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('Не удалось получить данные для отчета')
        }
        return await response.json()
    }
}
